<template>
  <div
    class="material-card item-card"
    :class="{ 'is-in-folder': inFolder, 'is-manageable': canManage }"
    :draggable="canManage"
    @click="$emit('click', material)"
    @dragstart="$emit('dragstart', material, $event)"
    @dragend="$emit('dragend', $event)"
  >
    <div class="mat-thumb">
      <button
        v-if="canManage"
        type="button"
        class="select-control"
        :class="{ active: isSelected }"
        :aria-label="isSelected ? `取消选择${material.name}` : `选择${material.name}`"
        @click.stop="$emit('toggle-select', material.id, $event)"
      >{{ isSelected ? '✓' : '' }}</button>

      <img
        v-if="imageSrc"
        :src="imageSrc"
        :alt="material.name"
        draggable="false"
        loading="lazy"
        @error="$emit('img-error', $event, material)"
      />
      <div v-else class="material-fallback" aria-hidden="true">无预览</div>

      <span class="zoom-hint">点击预览</span>

      <div v-if="canManage && showActions" class="card-actions">
        <button v-if="inFolder" type="button" class="btn-action" @click.stop="$emit('move-out', material)">移出</button>
        <button type="button" class="btn-action" @click.stop="$emit('rename', material)">重命名</button>
        <button v-if="canDelete" type="button" class="btn-action danger" @click.stop="$emit('delete', material)">删除</button>
      </div>
    </div>

    <div class="mat-body">
      <span class="material-name" :title="material.name">{{ material.name }}</span>
      <span class="material-cat">
        {{ catLabel }}
        <template v-if="isAllStores && storeName"> · {{ storeName }}</template>
        <template v-if="isFiltering && folderName"> · {{ folderName }}</template>
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  material: {
    type: Object,
    required: true,
  },
  canManage: {
    type: Boolean,
    default: false,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  inFolder: {
    type: Boolean,
    default: false,
  },
  isAllStores: {
    type: Boolean,
    default: false,
  },
  isFiltering: {
    type: Boolean,
    default: false,
  },
  storeName: {
    type: String,
    default: '',
  },
  folderName: {
    type: String,
    default: '',
  },
  catLabel: {
    type: String,
    default: '',
  },
  imageSrc: {
    type: String,
    default: '',
  },
});

defineEmits(['click', 'toggle-select', 'move-out', 'rename', 'delete', 'dragstart', 'dragend', 'img-error']);

const showActions = computed(() => props.inFolder || !props.isAllStores);
</script>

<style scoped>
.material-card {
  border-radius: 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  position: relative;
  cursor: pointer;
}

.material-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 12px 24px -4px rgba(15, 23, 42, 0.08);
}

.item-card.is-manageable { cursor: grab; }
.item-card.is-manageable:active { cursor: grabbing; }

.mat-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  min-height: 130px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #f1f5f9;
}

.mat-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
  box-sizing: border-box;
  pointer-events: none;
  transition: transform 0.25s ease;
}

.material-card:hover .mat-thumb img {
  transform: scale(1.05);
}

.mat-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: #ffffff;
}

.material-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-dark, #0f172a);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-cat {
  font-size: 12px;
  color: var(--text-muted, #64748b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.material-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-grey-light, #f1f5f9);
  color: var(--text-muted, #94a3b8);
  font-size: 12px;
  pointer-events: none;
}

.select-control {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 5;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.15s ease;
  cursor: pointer;
}

.select-control:hover {
  border-color: var(--color-primary);
}

.select-control.active {
  border-color: var(--color-primary);
  background: var(--color-primary);
}

.zoom-hint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  color: #ffffff;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}

.item-card:hover .zoom-hint {
  opacity: 1;
}

.card-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
  display: flex;
  gap: 4px;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.18s ease, transform 0.18s ease;
  pointer-events: none;
}

.material-card:hover .card-actions {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.btn-action {
  padding: 3px 10px;
  font-size: 11px;
  border-radius: 999px;
  border: 1px solid var(--border-color, #cbd5e1);
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
  white-space: nowrap;
  cursor: pointer;
}

.btn-action.danger {
  border-color: #ffa39e;
  background: #fff1f0;
  color: #cf1322;
}

.btn-action:hover {
  filter: brightness(0.97);
}
</style>
